import { Button } from 'antd';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  HomeIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import { selectLmsLink } from '../../../store/features/lmsLinkSlice';
import { useMemo } from 'react';
import { LmsLinkItemKey } from '../../../constants/types/lms/type';
import TextWinds from '../../../components/common/TextWinds';
import { selectLmsLists } from '../../../store/features/lmsSlice';
import { useParams } from 'react-router-dom';
import { openAlert } from '../../../store/features/modalSlice';
import { getCourseConfig } from '../../../api/utils/lms/utils';

const Resources = () => {
  const { classId } = useParams();

  const lmsLists = useAppSelector(selectLmsLists);

  const courseConfig =
    classId === lmsLists?.id ? getCourseConfig(lmsLists.course?.id) : undefined;

  const courseType = courseConfig?.group ?? '주일학교';

  const lmsLink = useAppSelector(selectLmsLink);

  const menualLink = useMemo(() => {
    return lmsLink.links.find(
      (link) => link.itemKey === LmsLinkItemKey.ARCHIVE_MANUAL,
    );
  }, [lmsLink]);

  const cafeLink = useMemo(() => {
    return lmsLink.links.find(
      (link) => link.itemKey === LmsLinkItemKey.ARCHIVE_CAFE,
    );
  }, [lmsLink]);

  const dispatch = useAppDispatch();

  const handleOpenLink = (link?: string | null) => {
    if (!link) {
      dispatch(
        openAlert({ message: '링크가 없습니다. 관리자에게 문의해주세요.' }),
      );
      return;
    }

    window.open(link, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex w-full flex-col pt-6 xl:px-5">
      <header className="flex flex-col gap-3 px-5 xl:flex-row xl:items-center xl:justify-between xl:px-0">
        <div className="min-w-0">
          <TextWinds type="title_h2" xlType="title_h3" className="mb-[2px]">
            자료실
          </TextWinds>

          <TextWinds type="content_body3">
            수업 운영에 필요한 교사용 메뉴얼과 수료생 카페 정보를 확인하세요.
          </TextWinds>
        </div>
      </header>

      <div className="my-5 flex flex-col gap-5 px-3 xl:px-0">
        <section className="rounded-xl bg-white p-5 shadow-card">
          <div className="mb-5 flex items-start gap-3">
            <div className="min-w-0">
              <TextWinds type="title_h3" color="gray9">
                {lmsLists.course.name} 교사용 메뉴얼
              </TextWinds>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[3fr_7fr]">
            <article className="flex min-h-[210px] flex-col rounded-xl border border-gray3 bg-gray0 p-5">
              <div className="flex items-center gap-2">
                <ArrowDownTrayIcon className="h-5 w-5 text-purple5" />

                <TextWinds
                  type="content_body2"
                  color="gray9"
                  className="font-bold"
                >
                  메뉴얼 다운로드
                </TextWinds>
              </div>

              <TextWinds
                type="content_body3"
                color="gray7"
                className="mt-3 block leading-6"
              >
                클래스 운영에 필요한 교사용 메뉴얼을 확인할 수 있습니다.
              </TextWinds>

              <div className="mt-4 border-t border-gray3 pt-4">
                <TextWinds
                  type="content_body3"
                  color="gray7"
                  className="font-bold"
                >
                  자료 구성
                </TextWinds>

                <TextWinds
                  type="content_body3"
                  color="gray7"
                  className="mt-1.5 block whitespace-pre-line leading-6"
                >
                  {menualLink.composition ||
                    '등록된 자료 구성 정보가 없습니다.'}
                </TextWinds>
              </div>

              <div className="mt-auto pt-5">
                <Button
                  type="primary"
                  block
                  disabled={!menualLink.link}
                  className="rounded-lg !border-purple5 !bg-purple5 hover:!border-purple6 hover:!bg-purple6 disabled:!border-gray4 disabled:!bg-gray2 disabled:!text-gray7"
                  onClick={() => handleOpenLink(menualLink.link)}
                >
                  <span className="flex items-center justify-center gap-1.5">
                    메뉴얼 다운로드 바로가기
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </span>
                </Button>
              </div>
            </article>

            <article className="min-h-[210px] rounded-xl border border-gray3 bg-gray0 p-5">
              <div className="flex items-center gap-2">
                <InformationCircleIcon className="h-5 w-5 text-purple5" />

                <TextWinds
                  type="content_body2"
                  color="gray9"
                  className="font-bold"
                >
                  메뉴얼 이용 안내
                </TextWinds>
              </div>

              <TextWinds
                type="content_body3"
                color="gray7"
                className="mt-3 block whitespace-pre-line leading-6"
              >
                {menualLink.notice || '등록된 이용 안내가 없습니다.'}
              </TextWinds>
            </article>
          </div>
        </section>

        <section className="rounded-xl bg-white p-5 shadow-card">
          <div className="mb-5 flex items-start gap-3">
            <div className="min-w-0">
              <TextWinds type="title_h3" color="gray9">
                {courseType} 수료생 카페
              </TextWinds>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[3fr_7fr]">
            <article className="flex min-h-[180px] flex-col rounded-xl border border-gray3 bg-gray0 p-5">
              <div className="flex items-center gap-2">
                <HomeIcon className="h-5 w-5 text-purple5" />

                <TextWinds
                  type="content_body2"
                  color="gray9"
                  className="font-bold"
                >
                  수료생 카페
                </TextWinds>
              </div>

              <TextWinds
                type="content_body3"
                color="gray7"
                className="mt-3 block leading-6"
              >
                {courseType} 수료생 카페에서 다양한 소식과 정보를 확인할 수
                있습니다.
              </TextWinds>

              <div className="mt-auto pt-5">
                <Button
                  type="primary"
                  block
                  disabled={!cafeLink.link}
                  className="rounded-lg !border-purple5 !bg-purple5 hover:!border-purple6 hover:!bg-purple6 disabled:!border-gray4 disabled:!bg-gray2 disabled:!text-gray7"
                  onClick={() => handleOpenLink(cafeLink.link)}
                >
                  <span className="flex items-center justify-center gap-1.5">
                    카페 바로가기
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </span>
                </Button>
              </div>
            </article>

            <article className="min-h-[180px] rounded-xl border border-gray3 bg-gray0 p-5">
              <div className="flex items-center gap-2">
                <InformationCircleIcon className="h-5 w-5 text-purple5" />

                <TextWinds
                  type="content_body2"
                  color="gray9"
                  className="font-bold"
                >
                  카페 이용 안내
                </TextWinds>
              </div>

              <TextWinds
                type="content_body3"
                color="gray7"
                className="mt-3 block whitespace-pre-line leading-6"
              >
                {cafeLink.notice || '등록된 카페 이용 안내가 없습니다.'}
              </TextWinds>
            </article>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Resources;
